import React from 'react';
import moment from 'moment';
import type { OrderLogEntry } from '@src/types/orderLogging';

export type OrderLogGridRow = OrderLogEntry & {
  deletedByLabel: string;
  tableOrGuest: string;
  deviceSummary: string;
  snapshotSummary: string;
  changeSummary: string;
  isTotalRow?: boolean;
};

const isTotalRow = (data?: OrderLogGridRow) =>
  !!(data?.isTotalRow || data?.id === 'Total:');

const formatMoney = (v: unknown) =>
  v === null || v === undefined || v === '' ? '—' : `₹${v}`;

export const toOrderLogGridRow = (entry: OrderLogEntry): OrderLogGridRow => {
  const snap = entry.snapshot || entry.next || {};
  const tableOrGuest =
    entry.orderKind === 'room'
      ? String(snap.guestName || snap.uoId || '—')
      : String(snap.tableNumber || '—');

  const device = entry.device || ({} as OrderLogEntry['device']);
  const deviceSummary = [
    device.isWebView ? 'WebView' : 'Browser',
    device.platform,
    device.connectionEffectiveType,
    device.viewportWidth && device.viewportHeight
      ? `${device.viewportWidth}x${device.viewportHeight}`
      : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const snapshotSummary =
    String(snap.cartSummary || '') ||
    [
      snap.orderSeqNo ? `Seq ${snap.orderSeqNo}` : '',
      snap.totalCost !== null && snap.totalCost !== undefined ? `₹${snap.totalCost}` : '',
      snap.selectedPayment || '',
    ]
      .filter(Boolean)
      .join(' · ');

  let changeSummary = snapshotSummary;
  if (entry.action === 'update' && (entry.previous || entry.next)) {
    const prev = entry.previous || {};
    const next = entry.next || {};
    changeSummary = [
      `Prev total ${formatMoney(prev.totalCost)} → Next ${formatMoney(next.totalCost)}`,
      prev.cartSummary || prev.guestName || prev.tableNumber
        ? `Prev: ${prev.cartSummary || prev.guestName || prev.tableNumber || ''}`
        : '',
      next.cartSummary || next.guestName || next.tableNumber
        ? `Next: ${next.cartSummary || next.guestName || next.tableNumber || ''}`
        : '',
    ]
      .filter(Boolean)
      .join(' | ');
  }

  return {
    ...entry,
    deletedByLabel:
      entry.deletedBy?.name || entry.deletedBy?.phone || entry.deletedBy?.uid || '—',
    tableOrGuest,
    deviceSummary,
    snapshotSummary,
    changeSummary,
  };
};

const DeviceRenderer = (props: { data?: OrderLogGridRow }) => {
  const { data } = props;
  if (!data || isTotalRow(data)) return React.createElement('span', null, '');
  const ua = data.device?.userAgent || '';
  return React.createElement(
    'span',
    {
      title: ua,
      style: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    data.deviceSummary || ua || '—'
  );
};

export const orderLoggingColDefs: any[] = [
  {
    headerName: 'Date / Time',
    field: 'deletedAtMs',
    width: 185,
    sortable: true,
    suppressMovable: true,
    comparator: (a: number, b: number) => (Number(a) || 0) - (Number(b) || 0),
    valueFormatter: (p: any) => {
      if (isTotalRow(p?.data)) return '';
      if (!p?.value) return '—';
      return moment(p.value).local().format('DD MMM, h:mm:ss a');
    },
  },
  {
    headerName: 'Kind',
    field: 'orderKind',
    width: 90,
    sortable: true,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? 'Total' : p?.data?.orderKind || '—',
  },
  {
    headerName: 'Action',
    field: 'action',
    width: 100,
    sortable: true,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.action || '—',
  },
  {
    headerName: 'Status',
    field: 'status',
    width: 100,
    sortable: true,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.status || '—',
    cellStyle: (p: any) => {
      if (isTotalRow(p?.data)) return null;
      if (p?.data?.status === 'failed') {
        return { color: '#b91c1c', fontWeight: 600 };
      }
      if (p?.data?.status === 'success') {
        return { color: '#047857', fontWeight: 600 };
      }
      return null;
    },
  },
  {
    headerName: 'Table / Guest',
    field: 'tableOrGuest',
    width: 140,
    sortable: true,
    suppressMovable: true,
    comparator: (a: string, b: string) =>
      String(a || '').localeCompare(String(b || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
  },
  {
    headerName: 'Order ID',
    field: 'orderId',
    width: 160,
    sortable: true,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.orderId || '—',
  },
  {
    headerName: 'UO / Seq',
    width: 130,
    sortable: true,
    suppressMovable: true,
    valueGetter: (p: any) => {
      if (isTotalRow(p?.data)) return '';
      const snap = p?.data?.snapshot || {};
      return snap.orderSeqNo || snap.uoId || '—';
    },
  },
  {
    headerName: 'Total',
    width: 100,
    sortable: true,
    suppressMovable: true,
    type: 'numericColumn',
    valueGetter: (p: any) => {
      if (isTotalRow(p?.data)) return p?.data?.snapshot?.totalCost ?? '';
      const total = p?.data?.snapshot?.totalCost;
      return total === null || total === undefined || total === '' ? '—' : total;
    },
  },
  {
    headerName: 'Deleted by',
    field: 'deletedByLabel',
    width: 150,
    sortable: true,
    suppressMovable: true,
  },
  {
    headerName: 'Source',
    field: 'source',
    width: 150,
    sortable: true,
    suppressMovable: true,
    valueGetter: (p: any) =>
      isTotalRow(p?.data) ? '' : p?.data?.source || '—',
  },
  {
    headerName: 'Device',
    field: 'deviceSummary',
    flex: 1,
    minWidth: 200,
    sortable: false,
    suppressMovable: true,
    cellRenderer: DeviceRenderer,
  },
  {
    headerName: 'Details / Change',
    field: 'changeSummary',
    flex: 1,
    minWidth: 220,
    sortable: false,
    suppressMovable: true,
    tooltipField: 'changeSummary',
  },
];
